/**
 * Preselection-source resolver — the ONE place that decides how a finder is
 * seeded, shared by both controllers so they stay consistent.
 *
 * After the `/datte` refactor (Phase 6) a seed is a SINGLE id (the deepest
 * selected node), not a name path. Priority (most-explicit-intent wins):
 * URL deep-link param > SSR preselect (`data-preselect`) > localStorage
 * (last actively-chosen id). First source yielding a real id wins.
 *
 * Each finder owns its OWN url param so the URL stays unambiguous about which
 * finder is in use:
 *   - SSR start-page dropdown → `datte_mf`
 *   - catalog button-column modal → `datte_mfm`
 * Both carry exactly one id (`?datte_mf=<id>`).
 *
 * **Strict id-parse guard (SEC-6-5, W0-4).** The param NAMES are retained across
 * the clean break, so an old bookmark `?datte_mf=Husqvarna/Motorsägen/...` is read
 * by the new parser. `Number('Husqvarna/…')` is `NaN`, which would flow into
 * `ensureChildren(NaN)`. We parse with the shared node-id rule (`parseNodeId`:
 * `/^-?\d+$/` → number, a 22-char base64url public id → string as-is, else
 * `null`) so a legacy name-path value resolves to "no seed" (empty finder), never
 * `NaN`. Negative ids are valid (`alt`'s virtual series nodes). The browser does
 * not know the active data source, so it accepts BOTH shapes and sends back what
 * it has — an id of the wrong kind 404s server-side, which is "no seed" too.
 *
 * @see ersatzteil-scraper docs/plans/2026-09-15 - interactive-shop-foundation-review/wave-0/W0-4-public-id-urls.md §3.4
 * @see docs/speed-optimization/research/2026-06-06 - datte-model-finder-endpoints/spec.md §3.8
 */

import { ModelFinderStateManager } from '../model-finder-state';
import { type NodeId, parseNodeId } from '../domain/brand';

/** The two deep-link param names — one per finder. */
export const MF_PARAM_SSR = 'datte_mf';
export const MF_PARAM_MODAL = 'datte_mfm';

/** Read the raw value of `param` from a query string, or null. */
function readParam(search: string, param: string): string | null {
  const pair = search
    .replace(/^\?/, '')
    .split('&')
    .find((p) => p.startsWith(`${param}=`));
  return pair ? decodeURIComponent(pair.slice(param.length + 1)) : null;
}

/**
 * Strict id parse: an integer string (optionally negative) → number, a 22-char
 * public id → string, anything else (incl. a legacy name-path value) → `null`.
 * Never returns `NaN`. Delegates to the ONE shared rule in `domain/brand.ts`.
 */
export function parseId(raw: string | null): NodeId | null {
  return parseNodeId(raw);
}

/** Whether the current URL carries a non-empty value for `param`. */
export function hasUrlParam(param: string): boolean {
  return !!readParam(window.location.search, param);
}

/** Parse the deep-link param into an id, or null (absent / legacy name-path). */
export function readUrlSeed(param: string): NodeId | null {
  return parseId(readParam(window.location.search, param));
}

/** Read the SSR-rendered preselect id from `data-preselect`, or null. */
export function readPreselect(root: HTMLElement): NodeId | null {
  return parseId(root.dataset.preselect ?? null);
}

/**
 * Resolve the initial seed id for a finder rooted at `root`, reading the
 * finder's own `urlParam` as the highest-priority source. Returns `null` when
 * idle.
 */
export function resolveSeed(root: HTMLElement, urlParam: string): NodeId | null {
  const fromUrl = readUrlSeed(urlParam);
  if (fromUrl !== null) return fromUrl;

  const preselect = readPreselect(root);
  if (preselect !== null) return preselect;

  return ModelFinderStateManager.load();
}

/**
 * Write `id` to the `param` URL param via replaceState (no history spam), or
 * remove the param when `id` is `null`. Other query params are preserved.
 */
export function writeUrlParam(param: string, id: NodeId | null): void {
  // `String(id)` erases the brand — the URL carries the id exactly as received:
  // a number as its digits, a public id unchanged (base64url is already URL-safe,
  // so `encodeURIComponent` in `setUrlParam` leaves it byte-identical).
  setUrlParam(param, id === null ? '' : String(id));
}

/** Remove `param` from the URL (e.g. when the modal closes). */
export function clearUrlParam(param: string): void {
  setUrlParam(param, '');
}

/** Curried writer for the ModelFinderCore `onAfterSelect` hook. */
export function makeUrlWriter(param: string): (id: NodeId | null) => void {
  return (id) => writeUrlParam(param, id);
}

/** Replace the `param` query value (or drop it when empty), preserving every other param. */
function setUrlParam(param: string, value: string): void {
  const others = window.location.search
    .replace(/^\?/, '')
    .split('&')
    .filter((p) => p && !p.startsWith(`${param}=`));
  if (value) others.push(`${param}=${encodeURIComponent(value)}`);

  const search = others.length ? `?${others.join('&')}` : '';
  const { protocol, host, pathname, hash } = window.location;
  window.history.replaceState(window.history.state, '', `${protocol}//${host}${pathname}${search}${hash}`);
}
